/**
 * AmbientesClienteSection — cadastro MANUAL de ambientes dentro do cliente.
 *
 * Catalogo proprio do cliente (desacoplado do orcamento). Leitura para quem ve
 * o cliente; criar/editar/excluir so com `comercial:gerir_ambientes` (gestores).
 * Cada ambiente: nome, preco, descricao + N modulos (nome, preco, quantidade).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiHome, FiPlus, FiTrash2, FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import {
  atualizarAmbienteCliente,
  criarAmbienteCliente,
  excluirAmbienteCliente,
  listarAmbientesCliente,
} from "../../../services/comercial";
import { formatarDinheiro, parseDinheiro } from "../../../utils/dinheiro";
import {
  moedaParaNumero,
  numeroParaMoeda,
} from "../../../utils/leadFormat";

function brl(valor) {
  const n = Number(valor) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function listaDe(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

const MODULO_VAZIO = { nome: "", descricao: "", preco: "", quantidade: "1" };

function AmbientesClienteSection({ request, clienteId, podeGerir }) {
  const [ambientes, setAmbientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null); // null | {} (novo) | ambiente
  const [form, setForm] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarAmbientesCliente(request, clienteId, {
        silentError: true,
      });
      setAmbientes(listaDe(data));
    } catch {
      setAmbientes([]);
    } finally {
      setLoading(false);
    }
  }, [request, clienteId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setForm({ nome: "", descricao: "", modulos: [] });
    setEditando("novo");
  };

  const abrirEdicao = (amb) => {
    setForm({
      nome: amb.nome || "",
      descricao: amb.descricao || "",
      modulos: (amb.modulos || []).map((m) => ({
        nome: m.nome || "",
        descricao: m.descricao || "",
        preco: m.preco != null ? numeroParaMoeda(m.preco) : "",
        quantidade: m.quantidade != null ? String(m.quantidade) : "1",
      })),
    });
    setEditando(amb);
  };

  const fechar = () => {
    setEditando(null);
    setForm(null);
  };

  const setCampo = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const setModulo = (i, campo, valor) =>
    setForm((f) => {
      const modulos = [...f.modulos];
      modulos[i] = { ...modulos[i], [campo]: valor };
      return { ...f, modulos };
    });

  const addModulo = () =>
    setForm((f) => ({ ...f, modulos: [...f.modulos, { ...MODULO_VAZIO }] }));

  const removeModulo = (i) =>
    setForm((f) => ({ ...f, modulos: f.modulos.filter((_, j) => j !== i) }));

  const salvar = async () => {
    const nome = (form.nome || "").trim();
    if (!nome) throw new Error("Informe o nome do ambiente.");
    // `preco` do ambiente nao vai no payload — o backend deriva da soma dos
    // modulos (preco x quantidade). Precos dos modulos vem mascarados (R$).
    const payload = {
      cliente: clienteId,
      nome,
      descricao: (form.descricao || "").trim(),
      modulos: (form.modulos || [])
        .filter((m) => (m.nome || "").trim())
        .map((m, i) => ({
          nome: m.nome.trim(),
          descricao: (m.descricao || "").trim(),
          preco: moedaParaNumero(m.preco) ?? 0,
          quantidade: m.quantidade === "" ? 1 : Number(m.quantidade),
          ordem: i,
        })),
    };
    if (editando === "novo") {
      await criarAmbienteCliente(request, payload);
    } else {
      await atualizarAmbienteCliente(request, editando.id, payload);
    }
    fechar();
    await carregar();
  };

  const excluir = async (amb) => {
    await excluirAmbienteCliente(request, amb.id);
    await carregar();
  };

  return (
    <section className="block" style={{ marginTop: 16 }}>
      <div className="block__head">
        <div className="block__title">
          <FiHome size={15} aria-hidden="true" style={{ marginRight: 6, verticalAlign: "-2px" }} />
          Ambientes do cliente
        </div>
        {podeGerir && (
          <button type="button" className="btn btn--secondary btn--sm" onClick={abrirNovo}>
            <FiPlus size={14} aria-hidden="true" /> Adicionar ambiente
          </button>
        )}
      </div>
      <div className="block__body">
        {loading ? (
          <div className="tiny muted" style={{ padding: 8 }}>Carregando…</div>
        ) : ambientes.length === 0 ? (
          <div className="tiny muted" style={{ padding: 8 }}>
            Nenhum ambiente cadastrado.
            {podeGerir ? " Use “Adicionar ambiente” para começar." : ""}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="dtable">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col />
                <col style={{ width: "90px" }} />
                <col style={{ width: "140px" }} />
                {podeGerir && <col style={{ width: "96px" }} />}
              </colgroup>
              <thead>
                <tr>
                  <th>Ambiente</th>
                  <th>Descrição</th>
                  <th className="num" style={{ textAlign: "center" }}>Módulos</th>
                  <th className="num" style={{ textAlign: "right" }}>Preço</th>
                  {podeGerir && <th aria-label="Ações" />}
                </tr>
              </thead>
              <tbody>
                {ambientes.map((amb) => (
                  <tr key={amb.id}>
                    <td><strong>{amb.nome}</strong></td>
                    <td className="muted" title={amb.descricao || ""}>
                      {amb.descricao || "—"}
                    </td>
                    <td className="num" style={{ textAlign: "center" }}>
                      {(amb.modulos || []).length}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>{brl(amb.preco)}</td>
                    {podeGerir && (
                      <td style={{ textAlign: "right", overflow: "visible" }}>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => abrirEdicao(amb)}
                          aria-label="Editar ambiente"
                        >
                          <FiEdit2 size={13} aria-hidden="true" />
                        </button>
                        <AsyncButton
                          variant="icon"
                          onClick={() => excluir(amb)}
                          successMessage="Ambiente excluído."
                          aria-label="Excluir ambiente"
                        >
                          <FiTrash2 size={13} aria-hidden="true" />
                        </AsyncButton>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SlideOverPanel
        open={editando !== null}
        onClose={fechar}
        title={editando === "novo" ? "Novo ambiente" : "Editar ambiente"}
        width="min(95vw, 560px)"
        footer={
          <>
            <button type="button" className="btn btn--secondary" onClick={fechar}>
              Cancelar
            </button>
            <AsyncButton onClick={salvar} successMessage="Ambiente salvo." silentError>
              Salvar
            </AsyncButton>
          </>
        }
      >
        {form && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label className="field">
              Nome do ambiente
              <input
                className="input"
                value={form.nome}
                onChange={(e) => setCampo("nome", e.target.value)}
                placeholder="Ex.: Cozinha planejada"
                autoFocus
              />
            </label>
            <label className="field">
              Descrição
              <textarea
                className="input"
                rows={3}
                value={form.descricao}
                onChange={(e) => setCampo("descricao", e.target.value)}
                placeholder="Material, acabamento, observações…"
              />
            </label>
            <div>
              <div className="flex between" style={{ alignItems: "center", marginBottom: 6 }}>
                <span className="tiny muted" style={{ fontWeight: 600 }}>
                  MÓDULOS{" "}
                  <span style={{ fontWeight: 400 }}>
                    · o preço do ambiente é a soma deles
                  </span>
                </span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={addModulo}>
                  <FiPlus size={13} aria-hidden="true" /> Módulo
                </button>
              </div>
              {form.modulos.length === 0 ? (
                <div className="tiny muted">Nenhum módulo. Opcional.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {form.modulos.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 90px 70px 28px",
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <input
                        className="input"
                        value={m.nome}
                        onChange={(e) => setModulo(i, "nome", e.target.value)}
                        placeholder="Nome do módulo"
                      />
                      <input
                        className="input"
                        inputMode="numeric"
                        value={m.preco}
                        onChange={(e) => setModulo(i, "preco", e.target.value)}
                  onBlur={(e) => {
                    // D-6A: formata no blur, nao a cada tecla.
                    const n = parseDinheiro(e.target.value);
                    setModulo(i, "preco", n === null ? "" : formatarDinheiro(n));
                  }}
                        placeholder="R$ 0,00"
                      />
                      <input
                        className="input"
                        type="number"
                        step="0.001"
                        min="0"
                        value={m.quantidade}
                        onChange={(e) => setModulo(i, "quantidade", e.target.value)}
                        placeholder="Qtd"
                      />
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => removeModulo(i)}
                        aria-label="Remover módulo"
                      >
                        <FiX size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Preço do ambiente = soma dos módulos (preço x qtd) — automático. */}
              <div
                className="flex between"
                style={{
                  marginTop: 12,
                  paddingTop: 8,
                  borderTop: "1px solid var(--border)",
                  fontWeight: 600,
                }}
              >
                <span className="tiny muted">TOTAL DO AMBIENTE</span>
                <span>
                  {numeroParaMoeda(
                    (form.modulos || []).reduce(
                      (s, m) =>
                        s +
                        (moedaParaNumero(m.preco) || 0) *
                          (Number(m.quantidade) || 0),
                      0,
                    ),
                  ) || "R$ 0,00"}
                </span>
              </div>
            </div>
          </div>
        )}
      </SlideOverPanel>
    </section>
  );
}

export default AmbientesClienteSection;
